/**
 * comparison-cards.js
 *
 * Card per viewport, gap, peek e navigazione letti dai data-attributes
 * impostati tramite editor Shopify (sections/comparison-cards.liquid).
 *
 *   Desktop (≥ 1025px)  : slides_desktop + gap_desktop + peek_desktop
 *                         → centrato se totalCards ≤ cols_desktop
 *                         → frecce visibili se show_nav_desktop = true (e in scroll mode)
 *   Tablet  (768–1024)  : slides_tablet + gap_mobile + peek_mobile (condiviso)
 *                         → centrato se totalCards ≤ cols_tablet
 *   Mobile  (< 768px)   : slides_mobile + gap_mobile + peek_mobile (condiviso)
 *                         → sempre scrollabile
 */

class ComparisonCardsSection extends HTMLElement {
  constructor() {
    super();
    this.swiper      = null;
    this.paginatorEl = null;
  }

  connectedCallback() {
    // Handler resize con debounce — equalizza altezze dopo ogni ridimensionamento
    this._onResize = this._debounce(() => {
      if (this.swiper) this.swiper.update();
      this.equalizeRows();
    }, 150);
    window.addEventListener('resize', this._onResize);

    if (typeof Swiper === 'undefined') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this._onResize);
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  init() {
    this.paginatorEl = this.querySelector('.cc-paginator');
    this.initSlider();
  }

  // ---------------------------------------------------------------------------
  // Utility: debounce
  // ---------------------------------------------------------------------------
  _debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  // ---------------------------------------------------------------------------
  // Equalizza l'altezza delle righe omologhe tra tutte le card così che i
  // divider si allineino orizzontalmente come in una tabella.
  //
  //   card 1: title (1 riga)  → row--title min-height = altezza card con più righe
  //   card 2: title (3 righe) → row--title min-height = stessa
  //
  // Viene chiamato dopo init, dopo breakpoint e su resize.
  // ---------------------------------------------------------------------------
  equalizeRows() {
    const types = ['title', 'chips', 'features'];

    // 1. Reset min-height per misura pulita
    types.forEach(type => {
      this.querySelectorAll(`.cc-card__row--${type}`)
        .forEach(el => (el.style.minHeight = ''));
    });

    // 2. Aspetta il layout aggiornato, poi applica
    requestAnimationFrame(() => {
      types.forEach(type => {
        const rows = Array.from(this.querySelectorAll(`.cc-card__row--${type}`));
        if (rows.length < 2) return;
        const maxH = rows.reduce((m, el) => Math.max(m, el.offsetHeight), 0);
        if (maxH > 0) rows.forEach(el => (el.style.minHeight = maxH + 'px'));
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Leggi i settings dall'editor (data-attributes impostati in Liquid)
  // Valori interi → float slidesPerView calcolato in JS
  // ---------------------------------------------------------------------------
  getViewportSettings() {
    const colsDesktop  = parseInt(this.getAttribute('data-cols-desktop')  || '3',  10);
    const colsTablet   = parseInt(this.getAttribute('data-cols-tablet')   || '2',  10);
    const colsMobile   = parseInt(this.getAttribute('data-cols-mobile')   || '1',  10);
    const gapDesktop   = parseInt(this.getAttribute('data-gap-desktop')   || '24', 10);
    const gapMobile    = parseInt(this.getAttribute('data-gap-mobile')    || '14', 10);
    const peekDesktop  = parseInt(this.getAttribute('data-peek-desktop')  || '20', 10);
    const peekMobile   = parseInt(this.getAttribute('data-peek-mobile')   || '20', 10); // condiviso mobile + tablet

    // "true" / "false" come stringa (Liquid serializza i boolean così)
    const navDesktop = this.getAttribute('data-nav-desktop') !== 'false';
    const navMobile  = this.getAttribute('data-nav-mobile')  === 'true';

    // slidesPerView con peek incorporato (es. 3 col + 20% → 3.2)
    const spvDesktop = colsDesktop + (peekDesktop / 100);
    const spvTablet  = colsTablet  + (peekMobile  / 100);
    const spvMobile  = colsMobile  + (peekMobile  / 100);

    return {
      colsDesktop, colsTablet, colsMobile,
      gapDesktop,  gapMobile,
      navDesktop,  navMobile,
      spvDesktop,  spvTablet,  spvMobile,
    };
  }

  // ---------------------------------------------------------------------------
  // Swiper
  // ---------------------------------------------------------------------------
  initSlider() {
    const swiperEl = this.querySelector('.comparison-cards__swiper');
    if (!swiperEl) return;

    const totalCards = parseInt(this.getAttribute('total-cards') || '1', 10);
    const {
      colsDesktop, colsTablet,
      gapDesktop,  gapMobile,
      spvDesktop,  spvTablet, spvMobile,
    } = this.getViewportSettings();

    const tabletCentered  = totalCards <= colsTablet;
    const desktopCentered = totalCards <= colsDesktop;

    // Impostazioni touch/velocità condivise tra tutti i breakpoint scrollabili
    const scrollTouch = {
      allowTouchMove:           true,
      grabCursor:               true,
      simulateTouch:            true,
      touchStartPreventDefault: false,
      passiveListeners:         true,
      resistanceRatio:          0.25,
      threshold:                4,
      longSwipesMs:             120,
      longSwipesRatio:          0.15,
      speed:                    320,
    };

    // Breakpoint centrato (auto width → CSS fissa a 320px, wrapper centrato)
    const makeCenteredBP = (spaceB) => ({
      slidesPerView:  'auto',
      spaceBetween:   spaceB,
      centeredSlides: false,
      allowTouchMove: false,
      grabCursor:     false,
    });

    // Breakpoint scrollabile — stesso feel del mobile
    const makeScrollBP = (perView, spaceB) => ({
      slidesPerView:  perView,
      spaceBetween:   spaceB,
      centeredSlides: false,
      ...scrollTouch,
    });

    this.swiper = new Swiper(swiperEl, {
      // Base — mobile (< 768px): sempre scrollabile
      slidesPerView:  spvMobile,
      spaceBetween:   gapMobile,
      centeredSlides: false,
      ...scrollTouch,

      observer:            true,
      observeParents:      true,
      watchSlidesProgress: true,

      navigation: {
        nextEl: this.querySelector('.comparison-cards__arrow--next'),
        prevEl: this.querySelector('.comparison-cards__arrow--prev'),
      },

      breakpoints: {
        768:  tabletCentered  ? makeCenteredBP(gapMobile)  : makeScrollBP(spvTablet,  gapMobile),
        1025: desktopCentered ? makeCenteredBP(gapDesktop) : makeScrollBP(spvDesktop, gapDesktop),
      },

      on: {
        init: (swiper) => {
          this.swiper = swiper;
          this.buildPaginator();
          this.updateCenteredAttr();
          this.equalizeRows();
        },
        breakpoint: () => {
          this.updateCenteredAttr();
          this.equalizeRows();
        },
        slideChange: () => this.updatePaginator(),
        resize:      () => { if (this.swiper) this.swiper.update(); },
      },
    });
  }

  // Aggiorna data-centered in base al breakpoint attivo
  updateCenteredAttr() {
    const totalCards = parseInt(this.getAttribute('total-cards') || '1', 10);
    const { colsDesktop, colsTablet } = this.getViewportSettings();
    const w = window.innerWidth;
    let centered = false;

    if (w >= 1025)      centered = totalCards <= colsDesktop;
    else if (w >= 768)  centered = totalCards <= colsTablet;
    // mobile: always scroll → centered = false

    this.setAttribute('data-centered', centered ? 'true' : 'false');
  }

  // ---------------------------------------------------------------------------
  // Paginator a strisce
  // ---------------------------------------------------------------------------
  buildPaginator() {
    if (!this.paginatorEl || !this.swiper) return;

    const slides = this.swiper.slides;
    this.paginatorEl.innerHTML = '';

    if (!slides || slides.length <= 1) return;

    slides.forEach((_, i) => {
      const dash = document.createElement('button');
      dash.className = 'cc-pag-dash' + (i === 0 ? ' active' : '');
      dash.setAttribute('role', 'tab');
      dash.setAttribute('aria-label', 'Slide ' + (i + 1));
      dash.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dash.addEventListener('click', () => this.swiper.slideTo(i));
      this.paginatorEl.appendChild(dash);
    });
  }

  updatePaginator() {
    if (!this.paginatorEl || !this.swiper) return;

    const dashes = this.paginatorEl.querySelectorAll('.cc-pag-dash');
    const active = this.swiper.realIndex ?? this.swiper.activeIndex ?? 0;

    dashes.forEach((d, i) => {
      const isActive = i === active;
      d.classList.toggle('active', isActive);
      d.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }
}

if (!window.customElements.get('comparison-cards-section')) {
  customElements.define('comparison-cards-section', ComparisonCardsSection);
}
